import Logger from './Logger';

export class UrlConstants {
  public static doubleSlash = '://';
  public static questionMark = '?';
  public static andMark = '&';
  public static slash = '/';
  public static hash = '#';
}

export class RelativeUrl {
  pathSegments: Array<string>;
  query: QueryStringMap;
  fragment: string;

  static decodeURISafe(url: string) {
    if (!url) {
      return url;
    }
    try {
      return decodeURI(url);
    } catch {
      Logger.warn(`Invalid uri ${url} was skipped.`);
    }

    return url;
  }

  constructor(pathName: string = '/', query: Map<string, string> = new Map<string, string>(), fragment: string = '') {
    this.path = pathName;
    this.query = QueryStringMap.fromMap(query);
    this.fragment = fragment;
  }

  public get path() {
    return `/${this.pathSegments.join('/')}`;
  }

  public set path(value: string) {
    this.pathSegments = !!value ? value.split('/').filter((x) => !!x) : [];
  }

  public static parse(urlString: string): RelativeUrl {
    if (urlString) {
      const schemaSeparatorIndex = urlString.indexOf(UrlConstants.doubleSlash);

      if (schemaSeparatorIndex >= 0) {
        const firstSingleSlashIndex = urlString.indexOf(
          UrlConstants.slash,
          schemaSeparatorIndex + UrlConstants.doubleSlash.length,
        );
        urlString = firstSingleSlashIndex >= 0 ? urlString.substring(firstSingleSlashIndex) : UrlConstants.slash;
      }
    }

    const decodeUrlString = RelativeUrl.decodeURISafe(urlString);
    const url = new RelativeUrl();
    const questionMarkIndex = decodeUrlString.indexOf('?');

    if (questionMarkIndex < 0) {
      url.path = decodeUrlString;
    } else {
      url.path = decodeUrlString.substring(0, questionMarkIndex);
      url.query = this.parseQuery(decodeUrlString.substring(questionMarkIndex + 1), true);
    }

    return url;
  }

  public static parseQuery(queryString: string, decoded: boolean = false): QueryStringMap {
    const decodeUrlString = decoded ? queryString : RelativeUrl.decodeURISafe(queryString);

    const query = new QueryStringMap();
    const segments =
      decodeUrlString[0] === '?'
        ? decodeUrlString.slice(1, decodeUrlString.length).split('&')
        : decodeUrlString.split('&');

    segments.forEach((segment) => {
      const parts = segment.split('=');
      if (parts.length === 2) {
        query.set(parts[0], parts[1]);
      }
    });

    return query;
  }

  public toString(shouldEncode: boolean = true): string {
    let result = this.path;
    result += RelativeUrl.queryToString(this.query);

    return shouldEncode ? encodeURI(result) : result;
  }

  public static queryToString(query: Map<string, string>) {
    let result = '';
    if (query.size > 0) {
      result += '?';
      let segments = new Array<string>();
      query.forEach((value, key) => {
        segments.push(`${key}=${value}`);
      });
      result += segments.join('&');
    }
    return result;
  }

  public clone(): RelativeUrl {
    return RelativeUrl.parse(this.toString());
  }

  public setQueryParameter(name: string, value: number | string) {
    this.query.set(name, value.toString());
    return this;
  }

  public isEmpty() {
    return this.pathSegments.length === 0 && this.query.size === 0;
  }

  public append(relative: RelativeUrl) {
    this.pathSegments.push(...relative.pathSegments);
    relative.query.forEach((value, key) => this.query.set(key, value));

    return this;
  }

  public setQuery(query: Map<string, string>) {
    this.query = QueryStringMap.fromMap(query);
  }
}

export class AbsoluteUrl {
  public schema: string;
  public hostname: string;
  public relative: RelativeUrl;

  constructor(schema: string, hostname: string, relative: RelativeUrl) {
    this.schema = schema;
    this.hostname = hostname;
    this.relative = relative;
  }

  public toString() {
    return `${this.schema}${UrlConstants.doubleSlash}${this.hostname}${
      this.relative.isEmpty() ? '' : this.relative.toString()
    }`;
  }

  public static parse(urlString: string) {
    if (!urlString) {
      throw Error(`Unable to parse absolute url, provided value is not defined, url = ${urlString}}`);
    }

    const doubleSlashIndex = urlString.indexOf(UrlConstants.doubleSlash);

    if (doubleSlashIndex < 0) {
      throw Error(`Unable to parse absolute url, provided value is invalid, url = ${urlString}`);
    }

    const firstSlashIndex = urlString.indexOf(UrlConstants.slash, doubleSlashIndex + UrlConstants.doubleSlash.length);

    const schema = urlString.substring(0, doubleSlashIndex);
    const hostname = urlString.substring(
      doubleSlashIndex + UrlConstants.doubleSlash.length,
      firstSlashIndex > 0 ? firstSlashIndex : undefined,
    );
    const relative = RelativeUrl.parse(urlString);

    return new AbsoluteUrl(schema, hostname, relative);
  }

  public append(relative: RelativeUrl) {
    this.relative.append(relative);
    return this;
  }
}

interface QueryStringParam {
  key: string;
  value: string;
}

export class QueryStringMap implements Map<string, string> {
  private map: Map<string, QueryStringParam>;

  constructor() {
    this.map = new Map<string, QueryStringParam>();
  }

  private normalizeKey(key: string): string {
    return key.toLowerCase();
  }

  set(key: string, value: string): this {
    this.map.set(this.normalizeKey(key), { key, value });
    return this;
  }

  get(key: string): string | undefined {
    return this.map.get(this.normalizeKey(key))?.value;
  }

  has(key: string): boolean {
    return this.map.has(this.normalizeKey(key));
  }

  delete(key: string): boolean {
    return this.map.delete(this.normalizeKey(key));
  }

  clear(): void {
    this.map.clear();
  }

  get size(): number {
    return this.map.size;
  }

  keys(): IterableIterator<string> {
    const keys = Array.from(this.map.values()).map((param) => param.key);
    return keys.values();
  }

  values(): IterableIterator<string> {
    const values = Array.from(this.map.values()).map((param) => param.value);
    return values.values();
  }

  entries(): IterableIterator<[string, string]> {
    const entries = Array.from(this.map.values()).map((param) => [param.key, param.value] as [string, string]);
    return entries.values();
  }

  [Symbol.iterator](): IterableIterator<[string, string]> {
    return this.entries();
  }

  // tslint:disable-next-line:no-any
  forEach(callbackfn: (value: string, key: string, map: Map<string, string>) => void, thisArg?: any): void {
    this.map.forEach((param) => {
      callbackfn.call(thisArg, param.value, param.key, this);
    });
  }

  get [Symbol.toStringTag](): string {
    return 'QueryStringMap';
  }

  public static fromMap(map: Map<string, string>): QueryStringMap {
    const queryStringMap = new QueryStringMap();
    map.forEach((value, key) => {
      queryStringMap.set(key, value);
    });

    return queryStringMap;
  }

  public toMap(): Map<string, string> {
    const map = new Map<string, string>();
    this.forEach((value, key) => {
      map.set(key, value);
    });

    return map;
  }
}
